import { Trip } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { buildFuelCellRow, DeviceWithFuelTank } from "../models/buildFuelCellRow";
import {
  FuelCellRow,
  FuelCellSample,
  FuelCellSummaryStats,
  LOW_FUEL_THRESHOLD_PERCENT,
} from "../models/FuelCellRow";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DIAGNOSTIC_FUEL_LEVEL_ID } from "@/constants/constants";
import { FUEL_CELL_GROUP_NAME } from "@/constants/groupTemplates";
import { useDevicesInGroup } from "@/hooks/groups/useDevicesInGroup";
import { useGroupByName } from "@/hooks/groups/useGroupByName";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";
import { fetchTripsComplete, GetTrips } from "@/utilities/query/fetchTripsComplete";

const DEVICE_FIELDS = [
  "id",
  "name",
  "licenseplate",
  "serialNumber",
  "vehicleIdentificationNumber",
  "fuelTankCapacity",
  "groups",
];

// Module-level so the defaults keep their identity and `rows` is not rebuilt on every render.
const NO_HISTORY = new Map<string, FuelCellSample[]>();
const NO_TRIPS = new Map<string, Trip[]>();

function sortSamples(samples: FuelCellSample[]): FuelCellSample[] {
  return [...samples].sort(
    (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
  );
}

/**
 * The fuel-cell fleet over a period.
 *
 * @param fromDate - Start of the period.
 * @param toDate - End of the period.
 * @param minDistanceKm - Distance below which no average consumption is reported, see
 * {@link averageConsumptionKgPer100Km}.
 * @param isReady - False until the persisted date range has loaded; nothing is fetched before.
 */
export function useFuelCellData(fromDate: Date, toDate: Date, minDistanceKm: number, isReady = true) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useGroupByName(FUEL_CELL_GROUP_NAME);

  const groupId = group?.id ?? null;

  const {
    data: devices = [],
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices,
    error: devicesError,
    isTruncated: devicesTruncated,
  } = useDevicesInGroup<DeviceWithFuelTank>(groupId, DEVICE_FIELDS);

  const deviceIds = useMemo(() => devices.map(d => d.id), [devices]);

  const {
    data: statusInfos = {},
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    error: statusError,
  } = useStatusInfosByIds(deviceIds, [DIAGNOSTIC_FUEL_LEVEL_ID]);

  const fromIso = useMemo(() => fromDate.toISOString(), [fromDate]);
  const toIso = useMemo(() => toDate.toISOString(), [toDate]);

  // One call for all vehicles: the client batches every Get into a shared ExecuteMultiCall, so a
  // failing call rejects the whole batch. There is no per-vehicle failure to isolate; the query
  // fails as a whole and the page shows the error (and retries through react-query).
  const {
    data: historyByDevice = NO_HISTORY,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
    error: historyError,
  } = useQuery({
    queryKey: ["fuelCellHistory", activeSessionId, deviceIds, fromIso, toIso],
    enabled: deviceIds.length > 0 && isReady,
    queryFn: async ({ signal }) => {
      const byDevice = new Map<string, FuelCellSample[]>(deviceIds.map(id => [id, []]));
      await fetchStatusDataComplete(geotabClient, {
        tasks: deviceIds.map(deviceId => ({
          key: deviceId,
          deviceId,
          diagnosticId: DIAGNOSTIC_FUEL_LEVEL_ID,
        })),
        fromDate,
        toDate,
        onPage: (key, page) => {
          const samples = byDevice.get(key);
          for (const record of page as FuelCellSample[]) samples?.push(record);
        },
        signal,
      });
      for (const [id, samples] of byDevice) byDevice.set(id, sortSamples(samples));
      return byDevice;
    },
    placeholderData: keepPreviousData,
  });

  const samplesByDevice = historyByDevice;

  // The distance comes off the trips. Reading it from the odometer at each end of the period,
  // which is what this page did, needs a reading to exist at both instants and reports nothing at
  // all where one is missing — or where the later one reads lower after a device swap.
  const {
    data: tripsByDevice = NO_TRIPS,
    isLoading: isLoadingTrips,
    isFetching: isFetchingTrips,
    error: tripsError,
  } = useQuery({
    queryKey: ["fuelCellTrips", activeSessionId, deviceIds, fromIso, toIso],
    enabled: deviceIds.length > 0 && isReady,
    queryFn: async ({ signal }) => {
      const getTripsFn: GetTrips = (search, resultsLimit) =>
        geotabClient.get("Trip", search, resultsLimit, signal) as Promise<Trip[]>;

      const trips = await fetchTripsComplete(getTripsFn, {
        fromDate,
        toDate,
        searches: deviceIds.map(id => ({ deviceSearch: { id } })),
        signal,
      });
      const byDevice = new Map<string, Trip[]>(deviceIds.map(id => [id, []]));
      for (const trip of trips) byDevice.get(trip.device.id)?.push(trip);
      return byDevice;
    },
    placeholderData: keepPreviousData,
  });

  const rows: FuelCellRow[] = useMemo(
    () =>
      devices.map(device =>
        buildFuelCellRow(
          device,
          historyByDevice.get(device.id),
          tripsByDevice.get(device.id),
          statusInfos[device.id]?.statusData?.find(
            entry => entry.diagnostic?.id === DIAGNOSTIC_FUEL_LEVEL_ID,
          ),
          minDistanceKm,
        ),
      ),
    [devices, statusInfos, historyByDevice, tripsByDevice, minDistanceKm],
  );

  const summaryStats: FuelCellSummaryStats = useMemo(() => {
    const totalUsedKg = rows.reduce((sum, r) => sum + (r.usedRangeKg ?? 0), 0);
    const totalRefills = rows.reduce((sum, r) => sum + (r.refillCount ?? 0), 0);
    const levels = rows.map(r => r.levelPercent).filter((v): v is number => v != null);
    const avgLevelPercent =
      levels.length > 0 ? levels.reduce((s, v) => s + v, 0) / levels.length : null;
    const lowFuelCount = rows.filter(
      r => r.levelPercent != null && r.levelPercent < LOW_FUEL_THRESHOLD_PERCENT,
    ).length;
    return {
      totalUsedKg,
      totalRefills,
      avgLevelPercent,
      lowFuelCount,
      deviceCount: rows.length,
    };
  }, [rows]);

  const groupMissing = !isLoadingGroup && !groupError && group === null;

  return {
    rows,
    samplesByDevice,
    isLoading:
      isLoadingGroup ||
      isLoadingDevices ||
      (deviceIds.length > 0 && (isLoadingStatus || isLoadingHistory || isLoadingTrips)),
    isFetching: isFetchingDevices || isFetchingStatus || isFetchingHistory || isFetchingTrips,
    error:
      (groupError as Error | null) ??
      (devicesError as Error | null) ??
      (statusError as Error | null) ??
      (historyError as Error | null) ??
      (tripsError as Error | null) ??
      null,
    groupMissing,
    devicesTruncated,
    summaryStats,
  };
}
